import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
    LayoutDashboard,
    Building2,
    Cloud,
    Workflow,
    ScrollText,
    LogOut,
    Network,
    Share2,
    Menu,
    X,
    FileCode2,
    Boxes,
    Terminal,
    Mail,
    BarChart3,
    HeartPulse,
    BellRing,
} from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";
import { GlobalSearch } from "@/components/GlobalSearch";

const NAV_GROUPS = [
    {
        label: "Monitor",
        items: [
            { to: "/", label: "Overview", icon: LayoutDashboard, end: true, testid: "nav-overview" },
            { to: "/health", label: "Health", icon: HeartPulse, testid: "nav-health" },
            { to: "/alerts", label: "Alert Center", icon: BellRing, testid: "nav-alerts" },
            { to: "/statistics", label: "Statistics", icon: BarChart3, testid: "nav-statistics" },
        ],
    },
    {
        label: "Fleet",
        items: [
            { to: "/resellers", label: "Resellers", icon: Building2, testid: "nav-resellers" },
            { to: "/clouds", label: "Clouds", icon: Cloud, testid: "nav-clouds" },
        ],
    },
    {
        label: "Infrastructure",
        items: [
            { to: "/infrastructure", label: "Infrastructure", icon: Network, testid: "nav-infra" },
            { to: "/topology", label: "Topology", icon: Share2, testid: "nav-topology" },
            { to: "/templates", label: "Templates", icon: Boxes, testid: "nav-templates" },
            { to: "/souvera", label: "Souvera", icon: Mail, testid: "nav-souvera" },
        ],
    },
    {
        label: "Platform",
        items: [
            { to: "/jobs", label: "Jobs", icon: Workflow, testid: "nav-jobs" },
            { to: "/audit", label: "Audit", icon: ScrollText, testid: "nav-audit" },
            { to: "/logs", label: "Logs", icon: Terminal, testid: "nav-logs" },
            { to: "/reference", label: "API Docs", icon: FileCode2, testid: "nav-api-docs" },
        ],
    },
];
// Hidden from the sidebar (routes stay reachable):
//  • /tokens (API Tokens) — verlinkt in der Infrastruktur-Seite
//  • /custom-apps, /import — vorübergehend ausgeblendet (evtl. später wieder)

function Wordmark({ size = "base" }) {
    return (
        <span className={`font-display tracking-tight ${size === "sm" ? "text-sm" : "text-base"}`}>
            <span className="text-cm-text">Nex</span>
            <span className="text-[#CCFF00]">Terminal</span>
        </span>
    );
}

function BrandMark({ small = false }) {
    return (
        <div
            className={`${small ? "w-5 h-5" : "w-7 h-7"} bg-[#CCFF00] flex items-center justify-center shrink-0`}
            aria-hidden="true"
        >
            <span className={`font-mono font-bold text-[#05070A] leading-none ${small ? "text-[10px]" : "text-[12px]"}`}>
                &gt;_
            </span>
        </div>
    );
}

export default function Layout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [open, setOpen] = useState(false);

    // Close drawer when route changes
    useEffect(() => {
        setOpen(false);
    }, [loc.pathname]);

    return (
        <div className="min-h-screen flex bg-cm-bg text-cm-text">
            {/* Mobile top bar */}
            <div
                className="lg:hidden fixed top-0 left-0 right-0 h-12 bg-cm-surface border-b border-cm-line z-40 flex items-center px-3 gap-3"
                data-testid="mobile-topbar"
            >
                <button
                    onClick={() => setOpen((v) => !v)}
                    className="cm-btn-ghost p-1.5"
                    data-testid="mobile-menu-toggle"
                    aria-label="Toggle navigation"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
                <BrandMark small />
                <Wordmark size="sm" />
            </div>

            {/* Sidebar (desktop fixed left; mobile drawer) */}
            <aside
                className={[
                    "w-64 border-r border-cm-line bg-cm-surface flex flex-col h-screen fixed left-0 top-0 z-50 transition-transform duration-200",
                    "lg:translate-x-0",
                    open ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
                ].join(" ")}
                data-testid="sidebar"
            >
                <div className="px-5 py-5 border-b border-cm-line flex items-center gap-3">
                    <BrandMark />
                    <div className="flex-1 min-w-0">
                        <Wordmark />
                        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-cm-dim mt-0.5">
                            Control Plane
                        </div>
                    </div>
                    <button
                        onClick={() => setOpen(false)}
                        className="lg:hidden cm-btn-ghost p-1"
                        aria-label="Close navigation"
                        data-testid="sidebar-close"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="px-3 py-3 border-b border-cm-line">
                    <GlobalSearch />
                </div>

                <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
                    {NAV_GROUPS.map((group) => (
                        <div key={group.label}>
                            <div className="px-3 pb-1.5 text-xs font-mono uppercase tracking-[0.18em] text-cm-dim">
                                {group.label}
                            </div>
                            <div className="space-y-0.5">
                                {group.items.map((n) => (
                                    <NavLink
                                        key={n.to}
                                        to={n.to}
                                        end={n.end}
                                        data-testid={n.testid}
                                        className={({ isActive }) =>
                                            [
                                                "flex items-center gap-3 px-3 py-2 text-[13px] font-medium border-l-2 transition-colors",
                                                isActive
                                                    ? "border-[#CCFF00] bg-cm-surface2 text-cm-text"
                                                    : "border-transparent text-cm-muted hover:text-cm-text hover:bg-cm-surface2/60",
                                            ].join(" ")
                                        }
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <n.icon
                                                    size={16}
                                                    strokeWidth={1.5}
                                                    className={isActive ? "text-[#CCFF00]" : ""}
                                                />
                                                <span>{n.label}</span>
                                            </>
                                        )}
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>

                <div className="border-t border-cm-line px-4 py-3">
                    <div className="cm-label">Signed in</div>
                    <div
                        className="text-xs text-cm-text truncate mt-1"
                        data-testid="sidebar-user-email"
                        title={user?.email}
                    >
                        {user?.email}
                    </div>
                    <div className="text-[11px] font-mono uppercase tracking-wider text-cm-dim mt-1">
                        {user?.role}
                    </div>
                    <button
                        data-testid="sidebar-logout-button"
                        onClick={async () => {
                            await logout();
                            nav("/login");
                        }}
                        className="mt-3 cm-btn-secondary w-full flex items-center justify-center gap-2 text-xs"
                    >
                        <LogOut size={14} strokeWidth={1.5} />
                        Sign out
                    </button>
                </div>
            </aside>

            {/* Mobile backdrop when drawer is open */}
            {open && (
                <button
                    onClick={() => setOpen(false)}
                    aria-label="Close navigation"
                    className="lg:hidden fixed inset-0 bg-black/70 z-40"
                    data-testid="sidebar-backdrop"
                />
            )}

            <main className="flex-1 min-h-screen w-full lg:ml-64 pt-12 lg:pt-0">
                <div className="cm-fade-in">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
